import axios from 'axios';
import { notify } from '../components/ui';
// Base URL works whether REACT_APP_API_URL includes "/api" or not.
// Local (empty) -> "/api" via the CRA dev proxy.
const RAW_API = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const API_BASE = RAW_API ? (RAW_API.endsWith('/api') ? RAW_API : RAW_API + '/api') : '/api';
const API = axios.create({ baseURL: API_BASE });

// --- Global loading bus: any in-flight API request drives the top loader. ---
let _active = 0;
const _loadCbs = new Set();
const _emitLoad = () => { const on = _active > 0; _loadCbs.forEach(fn => { try { fn(on); } catch (_) {} }); };
export const subscribeLoading = (fn) => { _loadCbs.add(fn); try { fn(_active > 0); } catch (_) {} return () => _loadCbs.delete(fn); };
const _incLoad = () => { _active++; _emitLoad(); };
const _decLoad = () => { _active = Math.max(0, _active - 1); _emitLoad(); };
API.interceptors.request.use(r => {
  _incLoad();
  const t = localStorage.getItem('token');
  if (t) r.headers.Authorization = `Bearer ${t}`;
  // Owners can operate on another branch; the server only honours this header
  // for the owner role, so it is safe to always send it.
  const b = localStorage.getItem('active_branch');
  if (b) r.headers['x-branch-id'] = b;
  // Multi-tenant: tell the backend which website/domain this request came from so
  // it can resolve the org. The server only uses this when there's no staff token.
  // For local testing you can force a tenant: localStorage.setItem('tenant_domain','acme.com').
  try {
    const td = localStorage.getItem('tenant_domain') || (typeof window !== 'undefined' ? window.location.hostname : '');
    if (td) r.headers['x-tenant-domain'] = td;
  } catch { /* ignore */ }
  return r;
});
API.interceptors.response.use(r => { _decLoad(); return r; }, e => { _decLoad(); const st = e.response?.status; if (st === 401) { localStorage.clear(); window.location.href = '/admin/login'; } else if (st === 403) { try { notify(e.response?.data?.error || 'You do not have permission for this action', 'error'); } catch (_) {} } return Promise.reject(e); });

// Auth
export const authMe = () => API.get('/auth/me');
export const markOnboarded = () => API.post('/auth/onboarded');
export const login          = d => API.post('/auth/login', d);
export const register       = d => API.post('/auth/register', d);
export const getProfile     = () => API.get('/auth/profile');
export const changePassword = d => API.put('/auth/change-password', d);

// Menu
export const getMenu        = p => API.get('/menu', { params: p });
export const uploadFile     = (formData) => API.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteUpload   = (data) => API.delete('/uploads', { data });
export const setMenuImage   = (id, d) => API.put(`/menu/${id}/image`, d);
export const createMenuItem = d => API.post('/menu', d);
export const updateMenuItem = (id,d) => API.put(`/menu/${id}`, d);
export const setMenuKitchenMode = (id, mode) => API.put(`/menu/${id}/kitchen`, { mode });
export const bulkUpdateMenuPrice = d => API.put('/menu/bulk/price', d);
export const deleteMenuItem = id => API.delete(`/menu/${id}`);
export const getSoldMenuIds = () => API.get('/menu/sold-item-ids');
export const bulkDeleteMenu = ids => API.post('/menu/bulk-delete', { ids });

// Categories
export const getCategories  = () => API.get('/categories');
export const createCategory = d => API.post('/categories', d);
export const deleteCategory = id => API.delete(`/categories/${id}`);

export const getMyOrders   = () => API.get('/orders/mine');

// Tables
export const getTables      = () => API.get('/tables');
export const createTable    = d => API.post('/tables', d);
export const updateTable    = (id,d) => API.put(`/tables/${id}`, d);
export const deleteTable    = id => API.delete(`/tables/${id}`);
export const freeTable      = id => API.put(`/tables/${id}/free`);

// Reservations
export const getReservations   = p => API.get('/reservations', { params: p });
export const createReservation = d => API.post('/reservations', d);
export const updateResvStatus  = (id,d) => API.put(`/reservations/${id}/status`, d);
export const deleteReservation = id => API.delete(`/reservations/${id}`);
export const getAvailableTables = p => API.get('/reservations/available-tables', { params: p });
export const getMyReservations  = () => API.get('/reservations/mine');

// Orders
export const getOrders      = p => API.get('/orders', { params: p });
export const getOrder       = id => API.get(`/orders/${id}`);
export const createOrder    = d => API.post('/orders', d);
export const updateOrderStatus = (id,d) => API.put(`/orders/${id}/status`, d);
export const acceptOrder        = id    => API.post(`/orders/${id}/accept`);
export const setOrderPriority = (id,priority) => API.put(`/orders/${id}/kitchen-priority`, { priority });
export const payOrder       = (id,d) => API.put(`/orders/${id}/pay`, d);
export const addOrderItem   = (id,d) => API.post(`/orders/${id}/items`, d);
export const removeOrderItem = (oid,iid) => API.delete(`/orders/${oid}/items/${iid}`);
// Cancel part of a cart line — n units off it, leaving the rest on the order.
// Adding an item only ever bumps its quantity now, so this is how a single unit
// gets pulled back off.
export const cancelOrderItemQty = (oid,iid,n) => API.post(`/orders/${oid}/items/${iid}/cancel`, { quantity: n });

// Kitchen
export const getKitchen     = () => API.get('/kitchen');
export const updateKitchenStatus = (id,d) => API.put(`/kitchen/${id}/status`, d);
export const updateKitchenPriority = (id,d) => API.put(`/kitchen/${id}/priority`, d);

// Bills
export const getBills       = () => API.get('/bills');
export const getBill        = id => API.get(`/bills/${id}`);
export const deleteBill     = id => API.delete(`/bills/${id}`);
export const getReceiptUrl  = (id, { by = 'bill', size = 'a4', download = false } = {}) => {
  const q = new URLSearchParams();
  if (by === 'order') q.set('by', 'order');
  if (size && size !== 'a4') q.set('size', size);
  if (download) q.set('download', '1');
  const qs = q.toString();
  return `/api/receipt/${id}${qs ? `?${qs}` : ''}`;
};

// Staff
export const getStaff       = () => API.get('/staff');
export const createStaff    = d => API.post('/staff', d);
export const updateStaff    = (id,d) => API.put(`/staff/${id}`, d);
export const deleteStaff    = id => API.delete(`/staff/${id}`);

// Members
export const getMembers     = p => API.get('/members', { params: p });
export const getMemberStats = id => API.get(`/members/${id}/stats`);
export const addPoints      = (id,d) => API.put(`/members/${id}/points`, d);
export const deleteMember   = id => API.delete(`/members/${id}`);

// Accounts
export const getAccounts    = () => API.get('/accounts');
export const toggleAccount  = id => API.put(`/accounts/${id}/toggle`);
export const deleteAccount  = id => API.delete(`/accounts/${id}`);

// Inventory
export const getInventory   = () => API.get('/inventory');
export const getItemLedger  = ({ inventory_id, from, to }) => API.get('/inventory/item-ledger', { params: { inventory_id, from, to } });
export const getAlerts      = () => API.get('/inventory/alerts');
export const createInventory = d => API.post('/inventory', d);
export const updateInventory = (id,d) => API.put(`/inventory/${id}`, d);
export const restockItem    = (id,d) => API.put(`/inventory/${id}/restock`, d);
export const deleteInventory = id => API.delete(`/inventory/${id}`);
export const bulkDeleteInventory = ids => API.post('/inventory/bulk-delete', { ids });

// Coupons
export const getCoupons     = () => API.get('/coupons');
export const applyOrderCoupon = (id, code) => API.put(`/orders/${id}/coupon`, { code });
export const validateCoupon = d => API.post('/coupons/validate', d);
export const getAvailableCoupons = p => API.get('/coupons/available', { params: p });
export const getWebCharges = p => API.get('/menu/web-charges', { params: p });
export const createCoupon   = d => API.post('/coupons', d);
export const updateCoupon   = (id,d) => API.put(`/coupons/${id}`, d);
export const deleteCoupon   = id => API.delete(`/coupons/${id}`);

// Stats
export const getDashboardStats = () => API.get('/stats/dashboard');
export const getTopItems       = p => API.get('/stats/top-items', { params: p });
export const getSalesSummary   = p => API.get('/stats/sales-summary', { params: p });
export const getHourlyStats    = () => API.get('/stats/hourly');

// Settings
export const getSettings    = () => API.get('/settings');
export const updateSettings = d => API.put('/settings', d);
export const setOrgLogo     = d => API.put('/settings/logo', d);
export const getCharges    = () => API.get('/settings/charges');
export const createCharge  = d => API.post('/settings/charges', d);
export const updateCharge  = (id, d) => API.put(`/settings/charges/${id}`, d);
export const deleteCharge  = id => API.delete(`/settings/charges/${id}`);
export const wipeMyData    = d => API.post('/settings/wipe-data', d);
export const getGstr1 = (from, to) => API.get('/gst/gstr1', { params: { from, to } });
export const getReportList = () => API.get('/report-engine/list');
export const runReport = (key, from, to) => API.get(`/report-engine/run/${key}`, { params: { from, to } });
export const getPnl = (from, to) => API.get('/report-engine/pnl', { params: { from, to } });
export const getIntegrationConfig = () => API.get('/integrations/config');
export const regenerateIntegrationKey = () => API.post('/integrations/regenerate-key');
// ---- Accounting ----
export const getAcctGroups = () => API.get('/accounting/groups');
export const addAcctGroup = d => API.post('/accounting/groups', d);
export const getLedgers = () => API.get('/accounting/ledgers');
export const addLedger = d => API.post('/accounting/ledgers', d);
export const autoBalanceOpening = (body = {}) => API.post('/accounting/opening-autobalance', body);
export const updateLedger = (id, d) => API.put(`/accounting/ledgers/${id}`, d);
export const getVoucher = id => API.get(`/accounting/vouchers/${id}`);
export const updateVoucher = (id, data) => API.put(`/accounting/vouchers/${id}`, data);
export const deleteVoucher = id => API.delete(`/accounting/vouchers/${id}`);
export const addVoucher = d => API.post('/accounting/vouchers', d);
export const getVouchers = params => API.get('/accounting/vouchers', { params });
export const getActivePeriod = () => API.get('/accounting/active-period');
export const getFinancialYears = () => API.get('/accounting/financial-years');
export const createFinancialYear = d => API.post('/accounting/financial-years', d);
export const activateFinancialYear = id => API.put(`/accounting/financial-years/${id}/activate`);
export const deleteFinancialYear = id => API.delete(`/accounting/financial-years/${id}`);
export const getNumberConfig = () => API.get('/accounting/number-config');
export const updateNumberConfig = items => API.put('/accounting/number-config', { items });
export const getTrialBalance = to => API.get('/accounting/trial-balance', { params: { to } });
export const getLedgerStatement = (id, from, to) => API.get(`/accounting/ledger/${id}`, { params: { from, to } });
export const getAcctPnl = to => API.get('/accounting/pnl', { params: { to } });
export const getBalanceSheet = to => API.get('/accounting/balance-sheet', { params: { to } });
export const getAcctDaybook = (from, to) => API.get('/accounting/daybook', { params: { from, to } });
export const syncBooks = () => API.post('/accounting/sync');
export const getCashflow = (from, to) => API.get('/accounting/cashflow', { params: { from, to } });
export const getOutstanding = as_on => API.get('/accounting/outstanding', { params: { as_on } });
// ---- Fixed Assets ----
export const getFixedAssets = as_on => API.get('/fixed-assets', { params: { as_on } });
export const addFixedAsset = d => API.post('/fixed-assets', d);
export const updateFixedAsset = (id, d) => API.put(`/fixed-assets/${id}`, d);
export const deleteFixedAsset = id => API.delete(`/fixed-assets/${id}`);
export const postDepreciation = as_on => API.post('/fixed-assets/post-depreciation', { as_on });
export const getBanks = () => API.get('/accounting/banks');
// Bank Statement Import + Reconciliation
export const getBankReconFields   = () => API.get('/bank-recon/fields');
export const parseBankFile        = (file) => { const fd = new FormData(); fd.append('file', file); return API.post('/bank-recon/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const previewBankImport    = d => API.post('/bank-recon/preview', d);
export const createBankStatement  = d => API.post('/bank-recon/statements', d);
export const getBankStatements    = () => API.get('/bank-recon/statements');
export const getBankStatement     = id => API.get(`/bank-recon/statements/${id}`);
export const deleteBankStatement  = (id, reverse) => API.delete(`/bank-recon/statements/${id}${reverse ? '?reverse=1' : ''}`);
export const rematchBankStatement = id => API.post(`/bank-recon/statements/${id}/rematch`);
export const getBankLineCandidates = id => API.get(`/bank-recon/lines/${id}/candidates`);
export const getBankLineBillCandidates = id => API.get(`/bank-recon/lines/${id}/bill-candidates`);
export const postBankLineBills    = (id, d) => API.post(`/bank-recon/lines/${id}/post-bills`, d);
export const matchBankLine        = (id, d) => API.post(`/bank-recon/lines/${id}/match`, d);
export const unmatchBankLine      = id => API.post(`/bank-recon/lines/${id}/unmatch`);
export const updateBankLine       = (id, d) => API.put(`/bank-recon/lines/${id}`, d);
export const deleteBankLine       = id => API.delete(`/bank-recon/lines/${id}`);
export const bulkDeleteBankLines  = (id, d) => API.post(`/bank-recon/statements/${id}/lines/bulk-delete`, d);
export const postBankLine         = (id, d) => API.post(`/bank-recon/lines/${id}/post`, d);
export const unpostBankLine       = id => API.post(`/bank-recon/lines/${id}/unpost`);
export const postAllBankLines     = id => API.post(`/bank-recon/statements/${id}/post-all`);
export const getBankBRS           = id => API.get(`/bank-recon/statements/${id}/brs`);
export const acceptAllSuggested   = id => API.post(`/bank-recon/statements/${id}/accept-all-suggested`);
export const lockBankStatement    = id => API.post(`/bank-recon/statements/${id}/lock`);
export const unlockBankStatement  = id => API.post(`/bank-recon/statements/${id}/unlock`);
export const approveBankLine      = id => API.post(`/bank-recon/lines/${id}/approve`);
export const getBankReconAudit    = id => API.get(`/bank-recon/statements/${id}/audit`);
export const getBankReconDashboard = () => API.get('/bank-recon/dashboard');
export const downloadBankBRSExcel = id => API.get(`/bank-recon/statements/${id}/brs.xlsx`, { responseType: 'blob' });
export const getBillRefs = ledgerId => API.get(`/accounting/bill-refs/${ledgerId}`);
export const getBillOutstanding = as_on => API.get('/accounting/bill-outstanding', { params: { as_on } });
// ---- Payroll ----
export const getEmployees = () => API.get('/payroll/employees');
export const addEmployee = d => API.post('/payroll/employees', d);
export const updateEmployee = (id, d) => API.put(`/payroll/employees/${id}`, d);
export const getPayrollRuns = () => API.get('/payroll/runs');
export const getPayrollRun = id => API.get(`/payroll/runs/${id}`);
export const runPayroll = period => API.post('/payroll/run', { period });
export const deletePayrollRun = id => API.delete(`/payroll/runs/${id}`);
export const postPayroll = id => API.post(`/payroll/runs/${id}/post`);

// Branches (multi-branch)
export const getBranches    = () => API.get('/branches');
export const createBranch   = d => API.post('/branches', d);
export const updateBranch   = (id,d) => API.put(`/branches/${id}`, d);
export const deleteBranch   = id => API.delete(`/branches/${id}`);

// ============ Phase 2: Expenses, Attendance, KOT ============
export const getExpenses     = p => API.get('/expenses', { params: p });
export const getExpenseSummary = () => API.get('/expenses/summary');
export const createExpense   = d => API.post('/expenses', d);
export const updateExpense   = (id,d) => API.put(`/expenses/${id}`, d);
export const deleteExpense   = id => API.delete(`/expenses/${id}`);

export const getAttendance   = p => API.get('/attendance', { params: p });
export const getAttendanceSummary = p => API.get('/attendance/summary', { params: p });
export const getAttendanceReport = p => API.get('/attendance/report', { params: p });
export const getAttendanceDaywise = p => API.get('/attendance/report/daywise', { params: p });
export const getShifts        = p => API.get('/shifts', { params: p });
export const createShift      = d => API.post('/shifts', d);
export const updateShift      = (id, d) => API.put('/shifts/' + id, d);
export const deleteShift      = id => API.delete('/shifts/' + id);
export const checkIn         = d => API.post('/attendance/check-in', d);
export const checkOut        = d => API.post('/attendance/check-out', d);
export const markAttendance  = d => API.post('/attendance/mark', d);
// Self-service attendance (any logged-in staff)
export const getMyAttToday   = () => API.get('/attendance/me/today');
export const getMyAttSummary = month => API.get('/attendance/me/summary', { params: { month } });
export const punchIn         = d => API.post('/attendance/punch-in', d);
export const punchOut        = d => API.post('/attendance/punch-out', d);
export const markMyLeave     = d => API.post('/attendance/me/leave', d);
// Payroll settings
export const getPayrollSettings  = () => API.get('/payroll/settings');
export const savePayrollSettings = d => API.put('/payroll/settings', d);

// KOT (kitchen ticket) print url — open in a new tab to print
export const getKotUrl = (orderId, size = 80) =>
  `${API.defaults.baseURL}/receipt/kot/${orderId}?size=${size}`;

// ============ Phase 3: Reviews, Payments, Delivery, Rewards ============
export const getReviews      = () => API.get('/reviews');
export const postReview      = d => API.post('/reviews', d);
export const getPublicReviews = p => API.get('/reviews/public', { params: p });
export const submitReview    = d => API.post('/reviews', d);
export const replyReview     = (id,d) => API.put(`/reviews/${id}/reply`, d);
export const publishReview   = (id,d) => API.put(`/reviews/${id}/publish`, d);
export const deleteReview    = id => API.delete(`/reviews/${id}`);

export const getPaymentConfig = () => API.get('/payments/config');
export const createRzpOrder   = d => API.post('/payments/create-order', d);
export const verifyPayment    = d => API.post('/payments/verify', d);
export const getUpiIntent     = d => API.post('/payments/upi-intent', d);

export const getDeliveries   = () => API.get('/delivery');
export const getMyDeliveries = () => API.get('/delivery/mine');
export const getDeliveryCollections = (date) => API.get('/delivery/collections', { params: date ? { date } : {} });
export const assignRider     = (id,d) => API.put(`/delivery/${id}/assign`, d);
export const updateDeliveryStatus = (id,d) => API.put(`/delivery/${id}/status`, d);

export const getBirthdaysToday = () => API.get('/rewards/birthdays/today');
export const getUpcomingBirthdays = p => API.get('/rewards/birthdays/upcoming', { params: p });
export const issueBirthdayReward = (id,d) => API.post(`/rewards/${id}/birthday-reward`, d);

// ============ Phase 4: Reports + Export ============
export const getRevenueComparison = () => API.get('/reports/revenue-comparison');
export const getStaffPerformance  = p => API.get('/reports/staff-performance', { params: p });
export const getItemMargin        = p => API.get('/reports/item-margin', { params: p });

// Authenticated file downloads (sends the Bearer token, unlike a raw <a href>).
function saveBlob(data, filename, type) {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}
export const downloadSalesXlsx = (days = 30) =>
  API.get(`/reports/export/sales.xlsx?days=${days}`, { responseType: 'blob' })
    .then(r => saveBlob(r.data, `sales-${days}d.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));
export const downloadSalesPdf = (days = 30) =>
  API.get(`/reports/export/sales.pdf?days=${days}`, { responseType: 'blob' })
    .then(r => saveBlob(r.data, `sales-summary-${days}d.pdf`, 'application/pdf'));

// ============ Bulk Item Import ============
export const downloadItemTemplate = () =>
  API.get('/import/menu/template', { responseType: 'blob' })
    .then(r => saveBlob(r.data, 'item-import-template.xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const exportMenuData = () =>
  API.get('/import/menu/export', { responseType: 'blob' }).then(r => saveBlob(r.data, 'menu-export.xlsx', XLSX_MIME));
export const exportInventoryData = () =>
  API.get('/import/inventory/export', { responseType: 'blob' }).then(r => saveBlob(r.data, 'inventory-export.xlsx', XLSX_MIME));
export const exportRecipeData = () =>
  API.get('/import/recipes/export', { responseType: 'blob' }).then(r => saveBlob(r.data, 'recipe-export.xlsx', XLSX_MIME));

export const downloadLedgerTemplate = () =>
  API.get('/import/ledgers/template', { responseType: 'blob' }).then(r => saveBlob(r.data, 'ledger-import-template.xlsx', XLSX_MIME));
export const exportLedgerData = () =>
  API.get('/import/ledgers/export', { responseType: 'blob' }).then(r => saveBlob(r.data, 'ledgers-export.xlsx', XLSX_MIME));
export const importLedgers = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post('/import/ledgers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const importItems = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post('/import/menu', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ============ Bulk Inventory Import ============
export const downloadInventoryTemplate = () =>
  API.get('/import/inventory/template', { responseType: 'blob' })
    .then(r => saveBlob(r.data, 'inventory-import-template.xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));

export const downloadRecipeTemplate = () =>
  API.get('/import/recipes/template', { responseType: 'blob' })
    .then(r => { const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'recipe-import-template.xlsx'; a.click(); URL.revokeObjectURL(url); });
export const importRecipes = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post('/import/recipes', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getUnits     = () => API.get('/units');
export const getSuppliers = (q) => API.get('/suppliers', { params: q ? { q } : {} });
export const createSupplier = (d) => API.post('/suppliers', d);
export const importInventory = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post('/import/inventory', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Open a PDF endpoint in a new tab by fetching it as a blob. This works in the
// CRA dev server (which won't proxy a direct browser navigation to /api) AND in
// production. The blank window is opened synchronously inside the click so the
// browser's popup blocker doesn't stop it.
function openPdfBlob(promise) {
  const win = window.open('', '_blank');
  // Returned so a caller can wait for the PDF to have actually been fetched —
  // printing a KOT is what marks its lines as sent, and the till needs to reload
  // the order afterwards to see that.
  return promise
    .then(r => {
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (win) win.location = url; else window.open(url, '_blank');
    })
    .catch(() => { if (win) win.document.write('Could not load the PDF. Make sure the order/bill exists.'); });
}
export const openPayslipPdf = (itemId) => openPdfBlob(API.get(`/payroll/payslip/${itemId}/pdf`, { responseType: 'blob' }));
export const openReceiptPdf = (id, { by = 'bill', size, download } = {}) => {
  const params = {};
  if (by) params.by = by;
  if (size) params.size = size;
  if (download) params.download = 1;
  openPdfBlob(API.get(`/receipt/${id}`, { params, responseType: 'blob' }));
};
export const openKotPdf = (orderId, size = 80) =>
  openPdfBlob(API.get(`/receipt/kot/${orderId}`, { params: { size }, responseType: 'blob' }));

// Auto-print a thermal receipt for a completed sale. Fetches the receipt PDF and
// sends it straight to the printer via a hidden iframe — no new tab, no page
// navigation. The browser print dialog appears unless the POS terminal runs
// Chrome with --kiosk-printing and a default (thermal) printer, in which case it
// prints with no dialog. For fully unattended network printing, pair with QZ Tray.
export const printReceiptSilently = (id, { by = 'order', size = '80' } = {}) =>
  API.get(`/receipt/${id}`, { params: { by, size }, responseType: 'blob' })
    .then(r => {
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0;';
      frame.src = url;
      frame.onload = () => {
        // Small delay lets Chrome's PDF viewer finish laying out before print().
        setTimeout(() => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (_) {} }, 300);
        // Clean up well after the print dialog has consumed the document.
        setTimeout(() => { try { document.body.removeChild(frame); } catch (_) {} URL.revokeObjectURL(url); }, 60000);
      };
      document.body.appendChild(frame);
    })
    .catch(() => {});

// ============ Inventory movements + Closing ============
export const addStockMovement = d => API.post('/inventory/movement', d);
export const getStockMovements = p => API.get('/inventory/movements', { params: p });
export const getClosingStock  = p => API.get('/inventory/closing', { params: p });

// ============ Production ============
export const getProduction    = p => API.get('/production', { params: p });
export const getProductionVoucher = id => API.get(`/production/${id}`);
export const createProduction = d => API.post('/production', d);

export const setStaffPermissions = (id, permissions) => API.put(`/staff/${id}/permissions`, { permissions });

// ============ Purchases ============
export const getPurchases   = p => API.get('/purchases', { params: p });
export const getPurchase    = id => API.get(`/purchases/${id}`);
export const createPurchase = d => API.post('/purchases', d);
export const updatePurchase = (id, d) => API.put(`/purchases/${id}`, d);
// ============ Inventory -> Menu (sellable) ============
export const sellInMenu     = (id, d) => API.post(`/inventory/${id}/sell-in-menu`, d);
export const removeFromMenu = id => API.delete(`/inventory/${id}/sell-in-menu`);

// ============ Recipes (menu item <-> inventory BOM) ============
export const getRecipe    = menuId => API.get(`/recipes/${menuId}`);
export const setRecipe    = (menuId, ingredients) => API.put(`/recipes/${menuId}`, { ingredients });
export const getRecipeMap = () => API.get('/recipes');
export const deleteRecipe = menuId => API.delete(`/recipes/${menuId}`);
export const bulkDeleteRecipes = ids => API.post('/recipes/bulk-delete', { ids });

// ============ SUPER ADMIN (SaaS platform) ============
export const saGetMe          = () => API.get('/superadmin/me');
export const saChangePassword = d => API.put('/superadmin/password', d);
export const saGetMIS         = () => API.get('/superadmin/mis');
export const saGetPackages    = () => API.get('/superadmin/packages');
export const saCreatePackage  = d => API.post('/superadmin/packages', d);
export const saUpdatePackage  = (id,d) => API.put(`/superadmin/packages/${id}`, d);
export const saDeletePackage  = id => API.delete(`/superadmin/packages/${id}`);
export const saGetOrgs        = p => API.get('/superadmin/organizations', { params: p });
export const saGetOrg         = id => API.get(`/superadmin/organizations/${id}`);
export const saGetConfig      = () => API.get('/superadmin/config');
export const saAddOrgDomain   = (id,d) => API.post(`/superadmin/organizations/${id}/domains`, d);
export const saDeleteOrgDomain= (id,domainId) => API.delete(`/superadmin/organizations/${id}/domains/${domainId}`);
export const saUpdateOrg      = (id,d) => API.put(`/superadmin/organizations/${id}`, d);
export const saExtendLicense  = (id,days) => API.post(`/superadmin/organizations/${id}/extend`, { days });
export const saRenewLicense   = (id,d) => API.post(`/superadmin/organizations/${id}/renew`, d);
export const saResetOrgPassword = (id,d) => API.post(`/superadmin/organizations/${id}/reset-password`, d);
export const saWipeOrgData    = (id,d) => API.post(`/superadmin/organizations/${id}/wipe-data`, d);
export const saGetAgreements  = () => API.get('/superadmin/agreements');
export const saCreateAgreement= d => API.post('/superadmin/agreements', d);
export const getSubscription   = () => API.get('/subscription');
export const requestPurchase   = d => API.post('/subscription/request', d);
export const renewSubscription = () => API.post('/subscription/renew');
export const getPayConfig      = () => API.get('/pay/config');
export const cancelPurchaseReq = id => API.post(`/subscription/request/${id}/cancel`);
export const getMyPayments     = () => API.get('/subscription/payments');
export const saGetSubSettings  = () => API.get('/superadmin/subscription-settings');
export const saSaveSubSettings = d => API.put('/superadmin/subscription-settings', d);
export const saGetPaymentReqs  = (status, org_id) => API.get('/superadmin/payment-requests', { params: { status, org_id } });
export const saGetPaymentClients = (params) => API.get('/superadmin/payment-clients', { params });
export const saApprovePayment  = id => API.post(`/superadmin/payment-requests/${id}/approve`);
export const saRejectPayment   = (id, d) => API.post(`/superadmin/payment-requests/${id}/reject`, d);

// ---- Module 3: Support Ticketing -----------------------------------------
// Customer (tenant) side
export const tkMine          = () => API.get('/tickets/mine');
export const tkUnseenCount   = () => API.get('/tickets/unseen-count');
export const tkRaise         = d => API.post('/tickets', d);
export const tkThread        = id => API.get(`/tickets/${id}`);
export const tkReply         = (id, d) => API.post(`/tickets/${id}/reply`, d);
export const tkClose         = (id, d) => API.post(`/tickets/${id}/close`, d);
// Agent / admin side
export const tkAgentList     = params => API.get('/tickets/agent/list', { params });
export const tkAgentUnseen   = params => API.get('/tickets/agent/unseen-count', { params });
export const tkAgentThread   = id => API.get(`/tickets/agent/${id}`);
export const tkAgentReply    = (id, d) => API.post(`/tickets/agent/${id}/reply`, d);
export const tkAgentAssign   = (id, d) => API.post(`/tickets/agent/${id}/assign`, d);
export const tkAgentResolve  = id => API.post(`/tickets/agent/${id}/resolve`);
export const tkAgentPriority = (id, d) => API.put(`/tickets/agent/${id}/priority`, d);
// Admin-only agent management
export const tkNavRights     = () => API.get('/tickets/nav-rights');
export const tkAgents        = () => API.get('/tickets/agents');
export const tkCreateAgent   = d => API.post('/tickets/agents', d);
export const tkUpdateAgent   = (id, d) => API.put(`/tickets/agents/${id}`, d);
export const tkDeactivateAgent = id => API.delete(`/tickets/agents/${id}`);

// ============ QR ordering ============
// Public (no auth needed — token in path)
export const getPublicR       = token => API.get(`/public/${token}`);
export const createPublicOrder = (token, data) => API.post(`/public/${token}/order`, data);
export const getPublicOrder    = (token, orderId) => API.get(`/public/${token}/order/${orderId}`);
export const payPublicOrder    = (token, orderId, data) => API.post(`/public/${token}/order/${orderId}/pay`, data);
// Admin QR config
export const getQrConfig  = () => API.get('/settings/qr');
export const saveQrConfig = data => API.post('/settings/qr', data);

// QR payment flows
export const claimPublicOrder   = (token, orderId, data) => API.post(`/public/${token}/order/${orderId}/claim`, data);
export const gatewayCreateOrder = (token, orderId) => API.post(`/public/${token}/order/${orderId}/gateway-order`, {});
export const gatewayVerifyOrder = (token, orderId, data) => API.post(`/public/${token}/order/${orderId}/gateway-verify`, data);

// ============ Customers (visit & order history) ============
export const getCustomers       = p => API.get('/customers', { params: p });
export const getCustomer        = phone => API.get(`/customers/${phone}`);
export const getCustomerOrders  = (phone, p) => API.get(`/customers/${phone}/orders`, { params: p });
export const setCustomerOptOut  = (phone, opted_out) => API.put(`/customers/${phone}/optout`, { opted_out });

// Auto-print a KOT (kitchen ticket) for an order — same hidden-iframe technique
// as printReceiptSilently, against the KOT endpoint. Used by the QR order alert.
export const printKotSilently = (orderId, { size = '80' } = {}) =>
  API.get(`/receipt/kot/${orderId}`, { params: { size }, responseType: 'blob' })
    .then(r => {
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const frame = document.createElement('iframe');
      frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0;';
      frame.src = url;
      frame.onload = () => {
        setTimeout(() => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (_) {} }, 300);
        setTimeout(() => { try { document.body.removeChild(frame); } catch (_) {} URL.revokeObjectURL(url); }, 60000);
      };
      document.body.appendChild(frame);
    });

// ============ Messaging (customer notifications) ============
export const getMsgProviders   = () => API.get('/messaging/providers');
export const createMsgProvider = d => API.post('/messaging/providers', d);
export const updateMsgProvider = (id, d) => API.put(`/messaging/providers/${id}`, d);
export const deleteMsgProvider = id => API.delete(`/messaging/providers/${id}`);
export const testMsgProvider   = (id, d) => API.post(`/messaging/providers/${id}/test`, d);
export const getMsgTemplates   = () => API.get('/messaging/templates');
export const createMsgTemplate = d => API.post('/messaging/templates', d);
export const updateMsgTemplate = (id, d) => API.put(`/messaging/templates/${id}`, d);
export const deleteMsgTemplate = id => API.delete(`/messaging/templates/${id}`);
export const previewMsgTemplate = d => API.post('/messaging/templates/preview', d);
export const getMsgOutbox      = p => API.get('/messaging/outbox', { params: p });
export const retryMsgOutbox    = id => API.post(`/messaging/outbox/${id}/retry`);
export const drainMsgOutbox    = () => API.post('/messaging/outbox/drain');
export const sendOrderMessage  = (orderId, d) => API.post(`/messaging/send/${orderId}`, d);
export const lookupCustomer     = phone => API.get(`/customers/lookup/${phone}`);
export const setOrderCustomer   = (id, d) => API.put(`/orders/${id}/customer`, d);
